import type { TriathlonProgression } from '@/types';
import { defaultTriathlonProgression } from '@/lib/store/constants';

type TriathlonWeeklyRhythm = NonNullable<TriathlonProgression['weeklyRhythm']>;

const DEFAULT_WEEKLY_RHYTHM = defaultTriathlonProgression.weeklyRhythm!;
const DEFAULT_SWIM_DAYS = DEFAULT_WEEKLY_RHYTHM.preferredDays.swim;
const ALL_WEEK_DAYS = [0, 1, 2, 3, 4, 5, 6];

function normalizeDays(days: number[] | undefined): number[] {
    if (!Array.isArray(days)) return [];
    return Array.from(
        new Set(days.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))
    ).sort((a, b) => a - b);
}

function fillDays(seedDays: number[], count: number): number[] {
    const filled = [...normalizeDays(seedDays)];
    for (const day of [...DEFAULT_SWIM_DAYS, ...ALL_WEEK_DAYS]) {
        if (filled.length >= count) break;
        if (!filled.includes(day)) {
            filled.push(day);
        }
    }
    return filled.slice(0, count);
}

export function resolveTriathlonWeeklyRhythm(
    progression?: Partial<Pick<TriathlonProgression, 'weeklyRhythm' | 'swimmingMetrics'>> | null
): TriathlonWeeklyRhythm {
    const current = progression?.weeklyRhythm;

    return {
        ...DEFAULT_WEEKLY_RHYTHM,
        ...current,
        frequencyTargets: {
            ...DEFAULT_WEEKLY_RHYTHM.frequencyTargets,
            ...current?.frequencyTargets,
            ...(current?.frequencyTargets?.swim === undefined && typeof progression?.swimmingMetrics?.swimFrequency === 'number'
                ? { swim: progression.swimmingMetrics.swimFrequency }
                : {}),
        },
        preferredDays: {
            ...DEFAULT_WEEKLY_RHYTHM.preferredDays,
            ...current?.preferredDays,
            swim: normalizeDays(current?.preferredDays?.swim ?? DEFAULT_WEEKLY_RHYTHM.preferredDays.swim),
            bike: normalizeDays(current?.preferredDays?.bike ?? DEFAULT_WEEKLY_RHYTHM.preferredDays.bike),
            run: normalizeDays(current?.preferredDays?.run ?? DEFAULT_WEEKLY_RHYTHM.preferredDays.run),
            strength: normalizeDays(current?.preferredDays?.strength ?? DEFAULT_WEEKLY_RHYTHM.preferredDays.strength),
        },
        anchorDays: {
            ...DEFAULT_WEEKLY_RHYTHM.anchorDays,
            ...current?.anchorDays,
            longBike: normalizeDays(current?.anchorDays?.longBike ?? DEFAULT_WEEKLY_RHYTHM.anchorDays.longBike).slice(0, 1),
            longRun: normalizeDays(current?.anchorDays?.longRun ?? DEFAULT_WEEKLY_RHYTHM.anchorDays.longRun).slice(0, 1),
        },
        fixedSessions: {
            ...DEFAULT_WEEKLY_RHYTHM.fixedSessions,
            ...current?.fixedSessions,
            swimLessons: Array.isArray(current?.fixedSessions?.swimLessons)
                ? current.fixedSessions.swimLessons
                    .filter((lesson) => Number.isInteger(lesson.day) && lesson.day >= 0 && lesson.day <= 6)
                    .map((lesson) => ({
                        day: lesson.day,
                        slot: lesson.slot === 'pm' ? ('pm' as const) : ('am' as const),
                    }))
                    .sort((a, b) => a.day - b.day)
                : DEFAULT_WEEKLY_RHYTHM.fixedSessions.swimLessons,
        },
    };
}

export function mergeTriathlonProgression(
    current: TriathlonProgression,
    patch: Partial<TriathlonProgression>
): TriathlonProgression {
    const nextSwimmingMetrics = patch.swimmingMetrics
        ? { ...current.swimmingMetrics, ...patch.swimmingMetrics }
        : current.swimmingMetrics;
    const currentWeeklyRhythm = resolveTriathlonWeeklyRhythm(current);
    const patchedWeeklyRhythm = patch.weeklyRhythm
        ? {
            ...currentWeeklyRhythm,
            ...patch.weeklyRhythm,
            frequencyTargets: {
                ...currentWeeklyRhythm.frequencyTargets,
                ...patch.weeklyRhythm.frequencyTargets,
            },
            preferredDays: {
                ...currentWeeklyRhythm.preferredDays,
                ...patch.weeklyRhythm.preferredDays,
            },
            anchorDays: {
                ...currentWeeklyRhythm.anchorDays,
                ...patch.weeklyRhythm.anchorDays,
            },
            fixedSessions: {
                ...currentWeeklyRhythm.fixedSessions,
                ...patch.weeklyRhythm.fixedSessions,
            },
        }
        : current.weeklyRhythm;

    const nextWeeklyRhythm = resolveTriathlonWeeklyRhythm({
        swimmingMetrics: nextSwimmingMetrics,
        weeklyRhythm: patchedWeeklyRhythm,
    });

    return {
        ...current,
        ...patch,
        swimmingMetrics: nextSwimmingMetrics,
        sessionCaps: patch.sessionCaps
            ? { ...(current.sessionCaps || {}), ...patch.sessionCaps }
            : current.sessionCaps,
        disciplineDistribution: patch.disciplineDistribution
            ? { ...current.disciplineDistribution, ...patch.disciplineDistribution }
            : current.disciplineDistribution,
        disciplineFocus: patch.disciplineFocus
            ? { ...current.disciplineFocus, ...patch.disciplineFocus }
            : current.disciplineFocus,
        progressionSettings: patch.progressionSettings
            ? {
                totalBaselineHours: patch.progressionSettings.totalBaselineHours
                    ?? current.progressionSettings?.totalBaselineHours
                    ?? defaultTriathlonProgression.progressionSettings?.totalBaselineHours,
                running: {
                    ...defaultTriathlonProgression.progressionSettings!.running,
                    ...current.progressionSettings?.running,
                    ...patch.progressionSettings.running,
                },
                cycling: {
                    ...defaultTriathlonProgression.progressionSettings!.cycling,
                    ...current.progressionSettings?.cycling,
                    ...patch.progressionSettings.cycling,
                },
                swimming: {
                    ...defaultTriathlonProgression.progressionSettings!.swimming,
                    ...current.progressionSettings?.swimming,
                    ...patch.progressionSettings.swimming,
                },
            }
            : current.progressionSettings,
        weeklyTargets: patch.weeklyTargets
            ? {
                ...patch.weeklyTargets,
                swimming: { ...patch.weeklyTargets.swimming },
                cycling: { ...patch.weeklyTargets.cycling },
                running: { ...patch.weeklyTargets.running },
                strength: { ...patch.weeklyTargets.strength },
            }
            : current.weeklyTargets,
        weeklyRhythm: nextWeeklyRhythm,
    };
}

export function getTriathlonSchedulingPreferences(
    progression?: Partial<Pick<TriathlonProgression, 'weeklyRhythm' | 'swimmingMetrics'>> | null,
    fallbackLongBikeDay?: number
) {
    const weeklyRhythm = resolveTriathlonWeeklyRhythm(progression);
    const fixedLessons = weeklyRhythm.fixedSessions.swimLessons;
    const fixedSwimDays = fixedLessons.map((lesson) => lesson.day);
    const swimFrequency = Math.max(
        1,
        fixedLessons.length,
        weeklyRhythm.frequencyTargets.swim
            ?? progression?.swimmingMetrics?.swimFrequency
            ?? DEFAULT_WEEKLY_RHYTHM.frequencyTargets.swim
            ?? 1
    );
    const preferredSwimDays = fillDays(
        fixedLessons.length > 0
            ? [...fixedSwimDays, ...weeklyRhythm.preferredDays.swim]
            : weeklyRhythm.preferredDays.swim,
        swimFrequency
    );
    const rawLongBikeDay = progression?.weeklyRhythm?.anchorDays?.longBike?.[0];
    const rawLongRunDay = progression?.weeklyRhythm?.anchorDays?.longRun?.[0];
    const longBikeDay = rawLongBikeDay
        ?? fallbackLongBikeDay
        ?? DEFAULT_WEEKLY_RHYTHM.anchorDays.longBike[0]
        ?? 6;
    const longRunDay = rawLongRunDay
        ?? DEFAULT_WEEKLY_RHYTHM.anchorDays.longRun[0]
        ?? (longBikeDay === 6 ? 0 : (longBikeDay + 1) % 7);
    const swimLessonSlotsByDay = Object.fromEntries(
        fixedLessons.map((lesson) => [lesson.day, lesson.slot])
    ) as Record<number, 'am' | 'pm'>;

    return {
        weeklyRhythm,
        swimFrequency,
        preferredSwimDays,
        preferredLongDays: [longBikeDay, longRunDay],
        swimLessonSlotsByDay,
    };
}
